import { apiClient } from './apiClient';

export interface BaremosData {
  id?: number;
  education_level?: string;
  age_group: string;
  percentile?: number;
  score_value?: number | string | null;
  /** Columna real usada por BNT (ver scoreFieldKey en BaremosTable) */
  score?: number | string | null;
  // Campos específicos de MoCA (rango de puntaje directo -> rango de percentil)
  score_min?: number;
  score_max?: number;
  percentile_min?: number;
  percentile_max?: number;
  country?: string;
  source?: string;
}

export interface EvaluationInfo {
  name: string;
  fullName: string;
  description: string;
  category: string;
  units?: string;
}

// Tipos específicos para Stroop
export interface StroopCondition {
  id: string;
  name: string;
  fullName: string;
  description: string;
  units: string;
  baremos: Record<string, BaremosData[]>;
  totalRecords: number;
  /** Nombre real de la tabla norms en la DB (para edición admin) */
  table?: string;
}

export type FluenciasCondition = StroopCondition;
export type HVLTRCondition = StroopCondition;
export type ReyFigureCondition = StroopCondition;
export type D2Condition = StroopCondition;
export type ElevatorCondition = StroopCondition;

export interface StroopBaremosResponse {
  success: boolean;
  data: {
    evaluation: EvaluationInfo;
    conditions: StroopCondition[];
    totalRecords: number;
  };
}

export interface BaremosResponse {
  success: boolean;
  data: {
    evaluation: EvaluationInfo;
    baremos: Record<string, BaremosData[]>;
    totalRecords: number;
  };
}

// IFS-Ch: tabla de referencia plana (Media ± DE de sujetos control por subtest),
// sin percentiles ni grupos etarios/educacionales.
export interface IFSChNormRow {
  id: number;
  display_order: number;
  subtest_es: string;
  subtest_en: string;
  mean_value: number;
  sd_value: number;
  country?: string;
  source?: string;
}

export interface IFSChBaremosResponse {
  success: boolean;
  data: {
    evaluation: EvaluationInfo;
    baremos: IFSChNormRow[];
    totalRecords: number;
  };
}

export interface StatsResponse {
  success: boolean;
  data: {
    evaluation: string;
    statistics: Array<{
      education_level?: string;
      age_group: string;
      total_records: number;
      records_with_scores: number;
      records_without_scores: number;
      min_percentile: number;
      max_percentile: number;
    }>;
    summary: {
      totalEducationLevels: number;
      totalAgeGroups: number;
      totalRecords: number;
    };
  };
}

class EvaluationService {
  /**
   * Obtener baremos de TMT-A
   */
  async getTMTABaremos(): Promise<BaremosResponse> {
    try {
      const response = await apiClient.get('/evaluations/tmt-a/baremos');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo baremos TMT-A:', error);
      throw error;
    }
  }

  /**
   * Obtener baremos de TMT-B
   */
  async getTMTBBaremos(): Promise<BaremosResponse> {
    try {
      const response = await apiClient.get('/evaluations/tmt-b/baremos');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo baremos TMT-B:', error);
      throw error;
    }
  }

  /**
   * Obtener estadísticas de TMT-A
   */
  async getTMTAStats(): Promise<StatsResponse> {
    try {
      const response = await apiClient.get('/evaluations/tmt-a/stats');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo estadísticas TMT-A:', error);
      throw error;
    }
  }

  /**
   * Obtener estadísticas de TMT-B
   */
  async getTMTBStats(): Promise<StatsResponse> {
    try {
      const response = await apiClient.get('/evaluations/tmt-b/stats');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo estadísticas TMT-B:', error);
      throw error;
    }
  }

  // =====================================================
  // STROOP TEST
  // =====================================================

  /**
   * Obtener todos los baremos de Stroop (4 condiciones)
   */
  async getStroopBaremos(): Promise<StroopBaremosResponse> {
    try {
      const response = await apiClient.get('/evaluations/stroop/baremos');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo baremos Stroop:', error);
      throw error;
    }
  }

  /**
   * Obtener baremos de Stroop - Palabra
   */
  async getStroopPalabraBaremos(): Promise<BaremosResponse> {
    try {
      const response = await apiClient.get('/evaluations/stroop/palabra/baremos');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo baremos Stroop Palabra:', error);
      throw error;
    }
  }

  /**
   * Obtener baremos de Stroop - Color
   */
  async getStroopColorBaremos(): Promise<BaremosResponse> {
    try {
      const response = await apiClient.get('/evaluations/stroop/color/baremos');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo baremos Stroop Color:', error);
      throw error;
    }
  }

  /**
   * Obtener baremos de Stroop - Palabra-Color
   */
  async getStroopPalabraColorBaremos(): Promise<BaremosResponse> {
    try {
      const response = await apiClient.get('/evaluations/stroop/palabra-color/baremos');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo baremos Stroop Palabra-Color:', error);
      throw error;
    }
  }

  /**
   * Obtener baremos de Stroop - Interferencia
   */
  async getStroopInterferenciaBaremos(): Promise<BaremosResponse> {
    try {
      const response = await apiClient.get('/evaluations/stroop/interferencia/baremos');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo baremos Stroop Interferencia:', error);
      throw error;
    }
  }

  // =====================================================
  // FLUENCIAS TEST
  // =====================================================

  /**
   * Obtener todos los baremos de Fluencias (4 categorías)
   */
  async getFluenciasBaremos(): Promise<StroopBaremosResponse> {
    try {
      const response = await apiClient.get('/evaluations/fluencias/baremos');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo baremos Fluencias:', error);
      throw error;
    }
  }

  // =====================================================
  // HVLT-R TEST
  // =====================================================

  /**
   * Obtener todos los baremos de HVLT-R
   */
  async getHVLTRBaremos(): Promise<StroopBaremosResponse> {
    try {
      const response = await apiClient.get('/evaluations/hvlt-r/baremos');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo baremos HVLT-R:', error);
      throw error;
    }
  }

  // =====================================================
  // REY FIGURE TEST
  // =====================================================

  /**
   * Obtener todos los baremos de Figura Compleja de Rey
   */
  async getReyFigureBaremos(): Promise<StroopBaremosResponse> {
    try {
      const response = await apiClient.get('/evaluations/rey-figure/baremos');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo baremos Rey Figure:', error);
      throw error;
    }
  }

  /**
   * Obtener todos los baremos de D2
   */
  async getD2Baremos(): Promise<StroopBaremosResponse> {
    try {
      const response = await apiClient.get('/evaluations/d2/baremos');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo baremos D2:', error);
      throw error;
    }
  }

  /**
   * Obtener baremos de Elevator (Appendix 3B, versiones A y B)
   */
  async getElevatorAppendix3BBaremos(): Promise<StroopBaremosResponse> {
    try {
      const response = await apiClient.get('/evaluations/elevator-appendix-3b/baremos');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo baremos Elevator Appendix 3B:', error);
      throw error;
    }
  }

  /**
   * Obtener baremos de MoCA (Evaluación Cognitiva de Montreal)
   */
  async getMOCABaremos(): Promise<BaremosResponse> {
    try {
      const response = await apiClient.get('/evaluations/moca/baremos');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo baremos MoCA:', error);
      throw error;
    }
  }

  /**
   * Obtener baremos de BNT (Boston Naming Test)
   */
  async getBNTBaremos(): Promise<BaremosResponse> {
    try {
      const response = await apiClient.get('/evaluations/bnt/baremos');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo baremos BNT:', error);
      throw error;
    }
  }

  /**
   * Obtener baremos de IFS-Ch (INECO Frontal Screening - Chile)
   */
  async getIFSChBaremos(): Promise<IFSChBaremosResponse> {
    try {
      const response = await apiClient.get('/evaluations/ifs-ch/baremos');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo baremos IFS-Ch:', error);
      throw error;
    }
  }

  /**
   * Obtener todas las conversiones aplicables para un percentil.
   */
  async getConversionsByPercentile(percentile: number, matrixType: string = 'original'): Promise<any[]> {
    try {
      const response = await apiClient.get(`/evaluations/matrix/percentile/${percentile}/conversions`, {
        params: { matrixType },
      });
      return response.data?.data ?? [];
    } catch (error) {
      console.error('Error obteniendo conversiones por percentil:', error);
      throw error;
    }
  }

  /**
   * Obtener clasificaciones aplicables para un percentil.
   */
  async getClassificationsByPercentile(percentile: number, matrixType: string = 'original'): Promise<string[]> {
    try {
      const response = await apiClient.get(`/evaluations/matrix/percentile/${percentile}/classifications`, {
        params: { matrixType },
      });
      return response.data?.data?.classifications ?? [];
    } catch (error) {
      console.error('Error obteniendo clasificaciones por percentil:', error);
      throw error;
    }
  }
}

export default new EvaluationService(); 